import {
    createAction,
    createAsyncThunk,
    createSlice,
    current
} from '@reduxjs/toolkit';
import { filtersAPI } from 'api/filtersAPI';
import { differenceInMonths } from 'date-fns';
import produce from 'immer';
import omit from 'lodash/omit';
import { FilterType } from 'types/types';
import {
    findAllDropdownItemsForMarket,
    findAllDropdownItemsForRm,
    findAllDropdownItemsForTeam,
    generateAllObjectForMarketsTeamsRms,
    transformFilterKeysToValuesAndLabels
} from 'utils/data';
import { generateMonthListByEndDate } from 'utils/helpers';

export const fetchZones = createAsyncThunk(
    'filters/fetchZones',
    async (params) => {
        return await filtersAPI.fetchZones(params);
    }
);
export const fetchFiltersInitialValues = createAsyncThunk(
    'filters/fetchFiltersInitialValues',
    async (params) => {
        return await filtersAPI.fetchFilteredValues(params);
    }
);

export const fetchInitialMainStats = createAsyncThunk(
    'filters/fetchInitialMainStats',
    async (params) => await filtersAPI.fetchMainStats(params)
);

export const fetchMainStats = createAsyncThunk(
    'filters/fetchMainStats',
    async (params) => await filtersAPI.fetchMainStats(params)
);

export const fetchFilteredDropdownValues = createAsyncThunk(
    'filters/fetchFilteredDropdownValues',
    async (params) => {
        const paramsForApi = omit(params.requestParams, [
            'selectedFilterType',
            'label',
            'value'
        ]);
        return await filtersAPI.fetchFilteredValues(paramsForApi);
    }
);

export const fetchLatestDataAvailableMonth = createAsyncThunk(
    'filters/fetchLatestDataAvailableMonth',
    async () => await filtersAPI.fetchLatestDataAvailableMonth()
);

export const updateFiltersWhenAllIsSelectedForTeamsOrRms = createAction(
    'filters/updateFiltersWhenAllIsSelectedForTeamsOrRms',
    (params) => {
        const { data, label, value, selectedFilterType, displayMapping } =
            params;
        const { rmRegions: markets, rmGroups: teams, rmList: rms } = data;
        const filteredMarkets = findAllDropdownItemsForMarket(
            markets,
            displayMapping.market
        );
        const filteredTeams = findAllDropdownItemsForTeam(
            teams,
            displayMapping.team
        );
        const filteredRms = findAllDropdownItemsForRm(rms, displayMapping.rm);
        return {
            payload: {
                label,
                value,
                selectedFilterType,
                markets: filteredMarkets,
                teams: filteredTeams,
                rms: filteredRms
            }
        };
    }
);

const filtersSlice = createSlice({
    name: 'filters',
    initialState: {
        zone: {
            label: 'All',
            value: 0,
            dropdownValues: null,
            stat: null
        },
        market: {
            label: 'All',
            value: null,
            dropdownValues: null,
            stat: null
        },
        team: {
            label: 'All',
            value: null,
            dropdownValues: null,
            stat: null
        },
        rm: {
            label: 'All',
            value: null,
            dropdownValues: null,
            stat: null
        },
        timeSlicer: {
            months: null,
            start: null,
            end: null,
            isSameMonth: false
        },
        allValues: {
            zone: { label: '', value: null, stat: null },
            market: { label: '', value: null, stat: null },
            team: { label: '', value: null, stat: null },
            rm: { label: '', value: null, stat: null }
        },
        displayMapping: {
            zone: null,
            market: null,
            team: null,
            rm: null
        },
        zonesFetched: false,
        isFetched: false,
        mainStatsFetched: false
    },
    reducers: {
        updateTimeslicerStartEndValues: (state, { payload }) =>
            produce(state, (draft) => {
                const { start, end } = payload;
                if (start && end) {
                    draft.timeSlicer.start = start;
                    draft.timeSlicer.end = end;
                    const monthDifference = differenceInMonths(
                        new Date(parseInt(end.year), end.monthNum, 1),
                        new Date(parseInt(start.year), start.monthNum, 1)
                    );
                    draft.timeSlicer.isSameMonth = monthDifference === 1;
                }
            }),
        updateFilter: (state, { payload }) =>
            produce(state, (draft) => {
                const { value, label, filterType } = payload;
                draft[filterType].value = value;
                draft[filterType].label = label;
            }),
        resetFilters: (state) =>
            produce(state, (draft) => {
                // reset main filters
                draft.market.value = state.allValues.market.value;
                draft.market.label = state.allValues.market.label;
                draft.market.dropdownValues = state.displayMapping.market;
                draft.market.stat = state.allValues.market.stat;

                draft.team.value = state.allValues.team.value;
                draft.team.label = state.allValues.team.label;
                draft.team.dropdownValues = state.displayMapping.team;
                draft.team.stat = state.allValues.team.stat;

                draft.rm.value = state.allValues.rm.value;
                draft.rm.label = state.allValues.rm.label;
                draft.rm.dropdownValues = state.displayMapping.rm;
                draft.rm.stat = state.allValues.rm.stat;

                draft.isFetched = false;
                draft.filtersInitialFetch = false;
                draft.mainStatsFetched = false;
            })
    },
    extraReducers: (builder) => {
        builder
            // <======= fetchLatestDataAvailableMonth =======>
            .addCase(
                fetchLatestDataAvailableMonth.fulfilled,
                (state, { payload }) =>
                    produce(state, (draft) => {
                        draft.timeSlicer.months = generateMonthListByEndDate(
                            payload.endMonth
                        );
                    })
            );
        // <======= /fetchLatestDataAvailableMonth =======>
        builder.addCase(fetchZones.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { zones } = payload;
                const zonesArray = zones.reduce(
                    (acc, curr) => [
                        ...acc,
                        { label: curr.zone, value: curr.zoneId }
                    ],
                    []
                );
                const allValueZoneObj = zones.find((z) => z.zone === 'All');
                draft.displayMapping.zone = zonesArray;
                draft.zone.dropdownValues = zonesArray;

                if (allValueZoneObj) {
                    draft.zone.value = allValueZoneObj.zoneId;
                    draft.zone.label = 'All';
                    draft.allValues.zone.value = allValueZoneObj.zoneId;
                    draft.allValues.zone.label = 'All';
                }
                draft.zonesFetched = true;
            })
        );
        builder.addCase(
            fetchFiltersInitialValues.fulfilled,
            (state, { payload }) =>
                produce(state, (draft) => {
                    const { rmRegions, rmGroups, rmList } = payload;
                    const marketsArray = transformFilterKeysToValuesAndLabels(
                        rmRegions,
                        'markets'
                    );
                    const teamsArray = transformFilterKeysToValuesAndLabels(
                        rmGroups,
                        'teams'
                    );
                    const rmsArray = transformFilterKeysToValuesAndLabels(
                        rmList,
                        'rms'
                    );
                    // Update display mappings
                    draft.displayMapping[FilterType.MARKET] = marketsArray;
                    draft.displayMapping[FilterType.TEAM] = teamsArray;
                    draft.displayMapping[FilterType.RM] = rmsArray;

                    // Update Market, Team and RM dropdown labels and values
                    draft[FilterType.MARKET].dropdownValues = marketsArray;
                    draft[FilterType.TEAM].dropdownValues = teamsArray;
                    draft[FilterType.RM].dropdownValues = rmsArray;
                    // Update Market, Team and RM 'All' value and label
                    const allValuesObj =
                        generateAllObjectForMarketsTeamsRms(payload);
                    draft[FilterType.MARKET].value = allValuesObj.market;
                    draft[FilterType.MARKET].label = 'All';
                    draft.allValues[FilterType.MARKET].value =
                        allValuesObj.market;
                    draft.allValues[FilterType.MARKET].label = 'All';

                    draft[FilterType.TEAM].value = allValuesObj.team;
                    draft[FilterType.TEAM].label = 'All';
                    draft.allValues[FilterType.TEAM].value = allValuesObj.team;
                    draft.allValues[FilterType.TEAM].label = 'All';

                    draft[FilterType.RM].value = allValuesObj.rm;
                    draft[FilterType.RM].label = 'All';
                    draft.allValues[FilterType.RM].value = allValuesObj.rm;
                    draft.allValues[FilterType.RM].label = 'All';

                    // Update filters data isFetched to true
                    draft.filtersInitialFetch = true;
                    draft.isFetched = true;
                })
        );
        builder.addCase(
            fetchFilteredDropdownValues.fulfilled,
            (state, { meta, payload }) =>
                produce(state, (draft) => {
                    const { requestParams } = meta.arg;
                    const { selectedFilterType, label, value } = requestParams;
                    const {
                        rmRegions: markets,
                        rmGroups: teams,
                        rmList: rms
                    } = payload;
                    if (selectedFilterType === FilterType.ZONE) {
                        const marketsArray =
                            transformFilterKeysToValuesAndLabels(
                                markets,
                                'markets'
                            );
                        const teamsArray = transformFilterKeysToValuesAndLabels(
                            teams,
                            'teams'
                        );
                        const rmsArray = transformFilterKeysToValuesAndLabels(
                            rms,
                            'rms'
                        );

                        // Update display mappings
                        draft.displayMapping[FilterType.MARKET] = marketsArray;
                        draft.displayMapping[FilterType.TEAM] = teamsArray;
                        draft.displayMapping[FilterType.RM] = rmsArray;

                        // Update Market, Team and RM dropdown labels and values
                        draft[FilterType.MARKET].dropdownValues = marketsArray;
                        draft[FilterType.TEAM].dropdownValues = teamsArray;
                        draft[FilterType.RM].dropdownValues = rmsArray;
                        // Update Market, Team and RM 'All' value and label
                        const allValuesObj =
                            generateAllObjectForMarketsTeamsRms(payload);
                        draft[FilterType.MARKET].value = allValuesObj.market;
                        draft[FilterType.MARKET].label = 'All';
                        draft.allValues[FilterType.MARKET].value =
                            allValuesObj.market;
                        draft.allValues[FilterType.MARKET].label = 'All';

                        draft[FilterType.TEAM].value = allValuesObj.team;
                        draft[FilterType.TEAM].label = 'All';
                        draft.allValues[FilterType.TEAM].value =
                            allValuesObj.team;
                        draft.allValues[FilterType.TEAM].label = 'All';

                        draft[FilterType.RM].value = allValuesObj.rm;
                        draft[FilterType.RM].label = 'All';
                        draft.allValues[FilterType.RM].value = allValuesObj.rm;
                        draft.allValues[FilterType.RM].label = 'All';

                        draft.zone.value = value;
                        draft.zone.label = label;
                    } else {
                        const unwrappedState = current(state);

                        // Filter filters based on new display mappings
                        const filteredMarkets = findAllDropdownItemsForMarket(
                            markets,
                            unwrappedState.displayMapping.market
                        );
                        const filteredTeams = findAllDropdownItemsForTeam(
                            teams,
                            unwrappedState.displayMapping.team
                        );
                        const filteredRms = findAllDropdownItemsForRm(
                            rms,
                            unwrappedState.displayMapping.rm
                        );
                        if (selectedFilterType === 'market') {
                            draft.market.value = value;
                            draft.market.label = label;

                            draft.team.value = draft.allValues.team.value;
                            draft.team.label = draft.allValues.team.label;

                            draft.rm.value = draft.allValues.rm.value;
                            draft.rm.label = draft.allValues.rm.label;

                            draft.team.dropdownValues = filteredTeams;
                            draft.rm.dropdownValues = filteredRms;
                        } else if (selectedFilterType === 'team') {
                            draft.market.value = filteredMarkets[0].value;
                            draft.market.label = filteredMarkets[0].label;

                            draft.team.value = value;
                            draft.team.label = label;

                            draft.rm.value = draft.allValues.rm.value;
                            draft.rm.label = draft.allValues.rm.label;

                            draft.rm.dropdownValues = filteredRms;
                        } else {
                            // selectedFilterType === 'rm'
                            draft.market.value = filteredMarkets[0].value;
                            draft.market.label = filteredMarkets[0].label;

                            draft.team.value = filteredTeams[0].value;
                            draft.team.label = filteredTeams[0].label;

                            draft.rm.value = value;
                            draft.rm.label = label;
                        }
                    }
                })
        );
        builder.addCase(fetchInitialMainStats.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { mainStatsReport } = payload;
                const { regionCount, groupCount, rmCount } = mainStatsReport;
                draft[FilterType.MARKET].stat = regionCount;
                draft.allValues[FilterType.MARKET].stat = regionCount;

                draft[FilterType.TEAM].stat = groupCount;
                draft.allValues[FilterType.TEAM].stat = groupCount;

                draft[FilterType.RM].stat = rmCount;
                draft.allValues[FilterType.RM].stat = rmCount;

                draft.mainStatsFetched = true;
            })
        );
        builder.addCase(fetchMainStats.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { mainStatsReport } = payload;
                const { regionCount, groupCount, rmCount } = mainStatsReport;

                draft[FilterType.MARKET].stat = regionCount;
                draft[FilterType.TEAM].stat = groupCount;
                draft[FilterType.RM].stat = rmCount;
            })
        );
        builder.addCase(
            updateFiltersWhenAllIsSelectedForTeamsOrRms,
            (state, { payload }) =>
                produce(state, (draft) => {
                    const { selectedFilterType, markets, teams, rms } = payload;

                    if (selectedFilterType === FilterType.TEAM) {
                        draft.market.value = markets[0].value;
                        draft.market.label = markets[0].label;

                        draft.team.value = draft.allValues.team.value;
                        draft.team.label = draft.allValues.team.label;

                        draft.rm.value = draft.allValues.rm.value;
                        draft.rm.label = draft.allValues.rm.label;

                        draft.market.dropdownValues =
                            draft.displayMapping.market;
                        draft.team.dropdownValues = teams;
                        draft.rm.dropdownValues = rms;
                    }

                    if (selectedFilterType === FilterType.RM) {
                        draft.market.value = markets[0].value;
                        draft.market.label = markets[0].label;

                        draft.rm.value = draft.allValues.rm.value;
                        draft.rm.label = draft.allValues.rm.label;

                        draft.market.dropdownValues =
                            draft.displayMapping.market;
                        draft.team.dropdownValues = teams;
                    }
                })
        );
    }
});
const { actions, reducer: filtersReducer } = filtersSlice;
export const { updateTimeslicerStartEndValues, updateFilter, resetFilters } =
    actions;
export default filtersReducer;
